import { NATIVE_CLIENT_HEADER, NATIVE_CLIENT_VALUE } from '@recipes/contracts';
import { isNativeApp } from '@/lib/platform';

const KEY = 'rm.refreshToken';

/**
 * Refresh-token transport of the native (Capacitor) app. The SameSite=Strict cookie is never
 * sent from capacitor://localhost, so the token is kept in Keychain (iOS) / Keystore-backed
 * storage (Android) and presented in a header. Browsers never touch this module's storage:
 * there the token stays in the HttpOnly cookie, out of reach of JavaScript.
 */
async function storage() {
  const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
  return SecureStoragePlugin;
}

export const nativeSession = {
  get enabled(): boolean {
    return isNativeApp();
  },

  /** Marks requests as coming from the native shell (the server then ignores cookies). */
  headers(): Record<string, string> {
    return isNativeApp() ? { [NATIVE_CLIENT_HEADER]: NATIVE_CLIENT_VALUE } : {};
  },

  async read(): Promise<string | null> {
    if (!isNativeApp()) return null;
    try {
      const { value } = await (await storage()).get({ key: KEY });
      return value || null;
    } catch {
      // The plugin rejects when the key does not exist.
      return null;
    }
  },

  async write(token: string): Promise<void> {
    if (!isNativeApp()) return;
    await (await storage()).set({ key: KEY, value: token });
  },

  async clear(): Promise<void> {
    if (!isNativeApp()) return;
    try {
      await (await storage()).remove({ key: KEY });
    } catch {
      // nothing stored
    }
  },
};
